/**
 * MyProfileContent - User profile settings panel
 * 
 * Allows users to view and edit their profile information,
 * change password, and manage account settings.
 */

import { useState, useEffect } from 'react';
import { authService, AuthUser } from '../../services/auth-service';
import { api } from '../../services/api';
import { PersonaPicker, AIParameterSlider } from '../PersonaPicker';
import { logger } from '../../utils/logger';
import { Button, Label, Input } from '@/components/ui';

interface MyProfileContentProps {
  userEmail?: string;
  userName?: string;
  userGroups?: string[];
}

export function MyProfileContent({
  userEmail: propEmail,
  userName: propName,
  userGroups: propGroups,
}: MyProfileContentProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form state
  const [displayName, setDisplayName] = useState(propName || '');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Persona state
  const [showPersonaPicker, setShowPersonaPicker] = useState(false);
  const [defaultPersonaId, setDefaultPersonaId] = useState<string>('aerospace-engineer');
  const [defaultPersonaName, setDefaultPersonaName] = useState<string>('Aerospace Engineer');
  const [currentTemperature, setCurrentTemperature] = useState<number>(0.2);
  
  const personaNames: Record<string, string> = {
    'aerospace-engineer': 'Aerospace Engineer',
    'aerospace-avionics': 'Avionics Software Engineer',
    'fiction-writer': 'Fiction Writer',
    'fiction-mystery': 'Mystery Writer',
    'fiction-scifi': 'Science Fiction Writer',
    'technical-writer': 'Technical Writer',
    'phd-researcher': 'PhD Researcher',
  };
  
  // Persona classification determines temperature lock behavior
  const personaSettings: Record<string, { 
    classification: 'regulated' | 'professional' | 'creative';
    defaultTemp: number;
    minTemp?: number;
    maxTemp?: number;
  }> = {
    'aerospace-engineer': { classification: 'regulated', defaultTemp: 0.2 },
    'aerospace-avionics': { classification: 'regulated', defaultTemp: 0.15 },
    'fiction-writer': { classification: 'creative', defaultTemp: 0.8, minTemp: 0.5, maxTemp: 0.95 },
    'fiction-mystery': { classification: 'creative', defaultTemp: 0.75, minTemp: 0.5, maxTemp: 0.95 },
    'fiction-scifi': { classification: 'creative', defaultTemp: 0.85, minTemp: 0.5, maxTemp: 0.95 },
    'technical-writer': { classification: 'professional', defaultTemp: 0.4, minTemp: 0.2, maxTemp: 0.6 },
    'phd-researcher': { classification: 'professional', defaultTemp: 0.5, minTemp: 0.3, maxTemp: 0.7 },
  };
  
  const currentPersonaSettings = personaSettings[defaultPersonaId] || { 
    classification: 'professional', 
    defaultTemp: 0.5 
  };
  
  // Load user data and preferences
  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
        if (currentUser?.name) {
          setDisplayName(currentUser.name);
        }
        
        // Load user preferences from API
        try {
          const prefResponse = await api.get('/user/preferences');
          const prefs = prefResponse.data?.data;
          const savedPersonaId = prefs?.defaultPersonaId;
          if (savedPersonaId) {
            setDefaultPersonaId(savedPersonaId);
            setDefaultPersonaName(personaNames[savedPersonaId] || savedPersonaId);
            // Load temperature (or use persona default)
            const settings = personaSettings[savedPersonaId];
            const savedTemp = prefs?.temperature ?? settings?.defaultTemp ?? 0.5;
            setCurrentTemperature(savedTemp);
          }
        } catch (prefError) {
          logger.error('Failed to load user preferences:', prefError);
        }
      } catch (_error) {
        logger.error('Failed to load user:', _error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- personaNames and personaSettings are stable after initial load
  }, []);
  
  const email = user?.email || propEmail || '';
  const groups = user?.groups || propGroups || [];
  
  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      // TODO: Implement profile update via Cognito
      // await authService.updateUserAttributes({ name: displayName });
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (_error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    try {
      // TODO: Implement password change via Cognito
      // await authService.changePassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (_error) {
      setMessage({ type: 'error', text: 'Failed to change password. Please check your current password.' });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading profile...</div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header - matches DocumentViewerContent styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">
          My Profile
        </h2>
      </div>
      
      {/* Content - matches DocumentViewerContent styling */}
      <div className="flex-1 overflow-y-auto p-4 bg-card space-y-4">
        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-success-light border border-success text-success'
              : 'bg-destructive-light border border-destructive text-destructive'
          }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}
        
        {/* Profile Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Profile Information
          </h3>
          
          {/* Avatar */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold">
              {email.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-foreground">
                {displayName || email.split('@')[0]}
              </p>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>
          </div>
          
          {/* Display Name */}
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-medium text-foreground mb-1">
                Display Name
              </Label>
              <Input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full"
                placeholder="Your display name"
              />
            </div>
            
            <div>
              <Label className="block text-sm font-medium text-foreground mb-1">
                Email Address
              </Label>
              <Input
                type="email"
                value={email}
                disabled
                className="w-full bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Email cannot be changed
              </p>
            </div>
            
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              size="sm"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </div>
        
        {/* Password Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Password
          </h3>
          
          {!showPasswordForm ? (
            <Button
              onClick={() => setShowPasswordForm(true)}
              variant="outline"
              size="sm"
            >
              Change Password
            </Button>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  Current Password
                </Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  New Password
                </Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  Confirm New Password
                </Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                  size="sm"
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
                <Button
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Default AI Persona Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Default AI Persona
          </h3>
          
          <p className="text-sm text-muted-foreground mb-4">
            Choose your default AI assistant persona. This determines the AI's expertise, writing style, and available tools for new projects.
          </p>
          
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
                <span className="text-link">✈</span>
                <span className="text-sm text-foreground">
                  {defaultPersonaName}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-destructive-light text-destructive">
                  Regulated
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPersonaPicker(true)}
            >
              Change
            </Button>
          </div>
          
          {/* PersonaPicker Modal */}
          <PersonaPicker
            isOpen={showPersonaPicker}
            onClose={() => setShowPersonaPicker(false)}
            onSelect={async (selectedPersonaId) => {
              setDefaultPersonaId(selectedPersonaId);
              setDefaultPersonaName(personaNames[selectedPersonaId] || selectedPersonaId);
              // Update temperature to new persona's default
              const newSettings = personaSettings[selectedPersonaId] || { defaultTemp: 0.5 };
              setCurrentTemperature(newSettings.defaultTemp);
              // Save to user preferences via API
              try {
                await api.patch('/user/preferences', { 
                  defaultPersonaId: selectedPersonaId,
                  temperature: newSettings.defaultTemp 
                });
                logger.debug('Saved default persona:', selectedPersonaId);
              } catch (_error) {
                logger.error('Failed to save default persona:', _error);
              }
            }}
            currentPersonaId={defaultPersonaId}
            title="Select Default AI Persona"
          />
          
          <p className="text-xs text-muted-foreground mt-3">
            Projects can override this default with their own persona setting.
          </p>
          
          {/* AI Writing Style Slider */}
          <div className="mt-6 pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">
              AI Writing Style
            </h4>
            <AIParameterSlider
              industryClassification={currentPersonaSettings.classification}
              temperatureLocked={currentPersonaSettings.classification === 'regulated'}
              defaultTemperature={currentPersonaSettings.defaultTemp}
              minTemperature={currentPersonaSettings.minTemp}
              maxTemperature={currentPersonaSettings.maxTemp}
              currentTemperature={currentTemperature}
              onTemperatureChange={async (value) => {
                setCurrentTemperature(value);
                // Save to user preferences
                try {
                  await api.patch('/user/preferences', { temperature: value });
                  logger.debug('Saved temperature:', value);
                } catch (_error) {
                  logger.error('Failed to save temperature:', _error);
                }
              }}
              onReset={() => {
                setCurrentTemperature(currentPersonaSettings.defaultTemp);
                api.patch('/user/preferences', { temperature: currentPersonaSettings.defaultTemp })
                  .catch(err => logger.error('Failed to reset temperature:', err));
              }}
              personaName={defaultPersonaName}
            />
          </div>
        </div>
        
        {/* Groups/Roles Section */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Roles & Permissions
          </h3>
          
          <div className="flex flex-wrap gap-2">
            {groups.length > 0 ? (
              groups.map((group) => (
                <span
                  key={group}
                  className="px-3 py-1 bg-info-light text-info rounded-full text-sm"
                >
                  {group}
                </span>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                No roles assigned
              </p>
            )}
          </div>
          
          <p className="text-xs text-muted-foreground mt-3">
            Contact an administrator to modify your roles
          </p>
        </div>
      </div>
    </div>
  );
}
